import React, { useState } from "react";
import { siteData } from "../mock";
import { ArrowRight, Clock, TrendingDown, UserRound, ShieldAlert, Wallet } from "lucide-react";
import PackageCheckoutModal from "./PackageCheckoutModal";

export default function InPersonPackages() {
  const { inPersonPackages, evaluation, dropIn, policies } = siteData;
  const [selected, setSelected] = useState(null);

  const starterTier = inPersonPackages[0];
  const upsell = {
    sessions: starterTier.maxSessions,
    savePerSession: dropIn.price - starterTier.hourlyRate,
    packageTotal: starterTier.maxSessions * starterTier.hourlyRate,
    dropInTotal: starterTier.maxSessions * dropIn.price,
  };

  const openDropInCheckout = () => {
    setSelected({
      upsell: { ...upsell, onSwitch: () => openTierCheckout(starterTier, upsell.sessions) },
      id: dropIn.id,
      title: "DROP-IN SESSION",
      subtitle: "1 in-person training session (1 hour) with Chris.",
      price: dropIn.price,
      priceNote: "single 1-hour session",
      savingsNote: null,
      requiredSessions: 1,
      totalSessions: 1,
      sessionLabel: "session",
      eventTitle: "1 HOUR TRAINING SESSION",
      eventDuration: "1 hour",
      calendlyUrl: siteData.calendly.session,
    });
  };

  const openEvaluationCheckout = () => {
    setSelected({
      id: evaluation.id,
      title: "EVALUATION",
      subtitle: "30-minute in-person assessment with Chris.",
      price: evaluation.price,
      priceNote: "one-time assessment",
      savingsNote: null,
      requiredSessions: 1,
      totalSessions: 1,
      sessionLabel: "assessment",
      eventTitle: "30 MINUTE ASSESSMENT",
      eventDuration: "30 min",
      calendlyUrl: siteData.calendly.assessment,
    });
  };

  const openTierCheckout = (tier, sessionCount) => {
    const count = sessionCount || tier.minSessions;
    const total = count * tier.hourlyRate;
    setSelected({
      id: tier.id,
      title: `${count} SESSION${count > 1 ? "S" : ""} PACKAGE`,
      subtitle: `${count} in-person training sessions at $${tier.hourlyRate}/hr.`,
      price: total,
      priceNote: `$${tier.hourlyRate} / hour`,
      savingsNote: null,
      requiredSessions: 1,
      totalSessions: count,
      sessionLabel: "session",
      eventTitle: "1 HOUR TRAINING SESSION",
      eventDuration: "1 hour",
      calendlyUrl: siteData.calendly.session,
      // Tier context so modal can show the session picker
      tier: {
        min: tier.minSessions,
        max: tier.maxSessions,
        hourlyRate: tier.hourlyRate,
      },
    });
  };

  const closeCheckout = () => setSelected(null);

  return (
    <section id="in-person" className="relative bg-[#0a0a0a] py-24">
      <div className="absolute inset-0 bg-grid-lines opacity-40 pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-2">
          <span className="deal-yellow font-heading font-semibold tracking-[0.3em] text-sm md:text-base">
            MANASSAS, VA BASED TRAINING
          </span>
        </div>
        <h2 className="font-display text-white font-bold text-5xl md:text-7xl tracking-wide mb-4">
          IN-PERSON PACKAGES
        </h2>
        <p className="text-white/70 text-lg mb-14">
          Pick how many sessions you want — the more you book, the less you pay per hour.
        </p>

        {/* Evaluation card — intro step */}
        <div className="mb-12 rounded-2xl card-surface p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
          <div className="flex items-start gap-5 flex-1">
            <div className="w-14 h-14 rounded-lg bg-[#e10600] flex items-center justify-center shrink-0">
              <UserRound size={28} className="text-white" strokeWidth={2.2} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="font-heading font-bold tracking-[0.25em] text-xs text-[#e10600]">
                  START HERE
                </p>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/8 text-white/80 text-[10px] font-heading font-semibold tracking-[0.15em]">
                  <Clock size={10} /> 30 MIN
                </span>
              </div>
              <h3 className="font-display font-bold text-white text-3xl md:text-4xl tracking-wide mb-2">
                EVALUATION
              </h3>
              <p className="text-white/70 text-base">
                Meet Chris, assess your fitness level, and map out a program that works for you.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div className="text-right">
              <div className="font-display font-bold text-white text-5xl leading-none">
                ${evaluation.price}
              </div>
              <p className="text-white/55 text-xs tracking-[0.2em] font-heading mt-1">
                ONE-TIME
              </p>
            </div>
            <button
              onClick={openEvaluationCheckout}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#e10600] hover:bg-[#ff1a1a] text-white font-heading font-bold tracking-[0.2em] text-sm transition-colors"
            >
              BOOK
              <ArrowRight size={16} />
            </button>
          </div>
        </div>

        {/* Drop-in card — single session */}
        <div data-testid="drop-in-card" className="-mt-6 mb-12 rounded-2xl card-surface p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
          <div className="flex items-start gap-5 flex-1">
            <div className="w-14 h-14 rounded-lg bg-[#e10600] flex items-center justify-center shrink-0">
              <Clock size={28} className="text-white" strokeWidth={2.2} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="font-heading font-bold tracking-[0.25em] text-xs text-[#e10600]">
                  NO COMMITMENT
                </p>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/8 text-white/80 text-[10px] font-heading font-semibold tracking-[0.15em]">
                  <Clock size={10} /> 1 HOUR
                </span>
              </div>
              <h3 className="font-display font-bold text-white text-3xl md:text-4xl tracking-wide mb-2">
                DROP-IN
              </h3>
              <p className="text-white/70 text-base">
                One 1-hour in-person training session. Book it whenever you need it.
              </p>
              <p data-testid="drop-in-savings-nudge" className="mt-2 flex flex-wrap items-center gap-x-1.5 text-sm text-white/85">
                <TrendingDown size={14} className="text-[#e10600]" />
                <span className="font-semibold text-white">Save ${upsell.savePerSession}/session.</span>
                Book {upsell.sessions} for ${upsell.packageTotal} instead of ${upsell.dropInTotal}.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div className="text-right">
              <div data-testid="drop-in-price" className="font-display font-bold text-white text-5xl leading-none">
                ${dropIn.price}
              </div>
              <p className="text-white/55 text-xs tracking-[0.2em] font-heading mt-1">
                1 SESSION
              </p>
            </div>
            <button
              data-testid="drop-in-book-btn"
              onClick={openDropInCheckout}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#e10600] hover:bg-[#ff1a1a] text-white font-heading font-bold tracking-[0.2em] text-sm transition-colors"
            >
              BOOK
              <ArrowRight size={16} />
            </button>
          </div>
        </div>

        {/* Pricing tiers heading */}
        <div className="mb-6">
          <p className="deal-yellow font-heading tracking-[0.3em] text-sm font-semibold mb-2">
            SESSION PACKAGES
          </p>
          <p className="text-white/70">
            Rates below are per hour. More sessions = lower hourly rate.
          </p>
        </div>

        {/* 5-tier grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-16">
          {inPersonPackages.map((tier) => {
            const isMax = tier.tag === "MAX VALUE";
            return (
              <div
                key={tier.id}
                className={`relative rounded-xl p-6 hover-lift flex flex-col ${
                  isMax
                    ? "bg-[#e10600] text-white border-2 border-[#e10600]"
                    : tier.tag === "POPULAR"
                    ? "card-surface border border-[#e10600]/40"
                    : "card-surface"
                }`}
              >
                {tier.tag && (
                  <span
                    className={`absolute -top-3 right-4 px-3 py-1 text-[10px] tracking-[0.2em] font-bold rounded ${
                      isMax ? "bg-[#0a0a0a] text-[#e10600]" : "bg-[#e10600] text-white"
                    }`}
                  >
                    {tier.tag}
                  </span>
                )}

                <p
                  className={`font-heading tracking-[0.25em] text-xs font-bold mb-1 ${
                    isMax ? "text-white/80" : "text-white/55"
                  }`}
                >
                  {tier.label}
                </p>
                <p
                  className={`font-heading font-bold text-sm tracking-[0.15em] mb-5 ${
                    isMax ? "text-white" : "text-white"
                  }`}
                >
                  {tier.minSessions}–{tier.maxSessions} SESSIONS
                </p>

                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span
                      className={`font-display font-bold text-5xl leading-none ${
                        isMax ? "text-white" : "text-white"
                      }`}
                    >
                      ${tier.hourlyRate}
                    </span>
                    <span
                      className={`text-xs font-heading tracking-[0.15em] ${
                        isMax ? "text-white/80" : "text-white/60"
                      }`}
                    >
                      /HR
                    </span>
                  </div>
                  <p
                    className={`text-xs mt-2 ${
                      isMax ? "text-white/85" : "text-white/55"
                    }`}
                  >
                    Up to ${(tier.maxSessions * tier.hourlyRate).toLocaleString()}
                  </p>
                </div>

                <p
                  className={`text-xs leading-relaxed mb-5 flex-1 ${
                    isMax ? "text-white/90" : "text-white/70"
                  }`}
                >
                  {tier.note}
                </p>

                <button
                  onClick={() => openTierCheckout(tier)}
                  className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-heading font-bold tracking-[0.2em] text-xs transition-colors ${
                    isMax
                      ? "bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white"
                      : "bg-[#e10600] hover:bg-[#ff1a1a] text-white"
                  }`}
                >
                  SELECT
                  <ArrowRight size={14} />
                </button>
              </div>
            );
          })}
        </div>

        {/* Policies strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Payment policy */}
          <div className="rounded-xl card-surface p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-md bg-[#e10600] flex items-center justify-center">
                <Wallet size={18} className="text-white" strokeWidth={2.4} />
              </div>
              <h4 className="font-heading font-bold text-white text-sm tracking-[0.25em]">
                {policies.payment.heading}
              </h4>
            </div>
            <p className="text-white/75 text-sm leading-relaxed mb-4">
              {policies.payment.body}
            </p>
            <div className="flex flex-wrap gap-2">
              {policies.payment.methods.map((m) => (
                <span
                  key={m}
                  className="inline-flex items-center px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-white/85 text-xs font-heading font-semibold tracking-[0.1em]"
                >
                  {m}
                </span>
              ))}
            </div>
          </div>

          {/* Cancellation policy */}
          <div className="rounded-xl card-surface p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-md bg-[#e10600] flex items-center justify-center">
                <ShieldAlert size={18} className="text-white" strokeWidth={2.4} />
              </div>
              <h4 className="font-heading font-bold text-white text-sm tracking-[0.25em]">
                {policies.cancellation.heading}
              </h4>
            </div>
            <p className="text-white/75 text-sm leading-relaxed mb-3">
              {policies.cancellation.body}
            </p>
            <div className="rounded-md border border-[#e10600]/40 bg-[#e10600]/10 px-3 py-2.5 flex items-start gap-2">
              <TrendingDown size={14} className="text-[#e10600] shrink-0 mt-0.5" />
              <p className="text-white/90 text-xs leading-relaxed">
                <span className="font-semibold text-white">Within 3 hours of session time:</span>{" "}
                {policies.cancellation.strict}
              </p>
            </div>
          </div>
        </div>
      </div>

      <PackageCheckoutModal
        isOpen={selected !== null}
        onClose={closeCheckout}
        item={selected}
        onSessionCountChange={(count) => {
          // Keep selected item in sync when the modal's session picker changes
          if (!selected || !selected.tier) return;
          const total = count * selected.tier.hourlyRate;
          setSelected({
            ...selected,
            title: `${count} SESSION${count > 1 ? "S" : ""} PACKAGE`,
            subtitle: `${count} in-person training sessions at $${selected.tier.hourlyRate}/hr.`,
            price: total,
            totalSessions: count,
          });
        }}
      />
    </section>
  );
}
